/**
 * @file crypto.js
 * @description Contains helper functions for cryptographic operations like key derivation,
 * encryption, and decryption using the Web Crypto API.
 */

// --- NEW: Base64 helper functions (copied from your extension for consistency) ---

/**
 * Normalizes a Base64 string, making it compatible with atob()
 * by replacing URL-safe characters.
 * @param {string} b64 - The Base64 string.
 * @returns {string} A standard Base64 string.
 */
function normalizeB64(b64) {
    if (typeof b64 !== 'string') return '';
    let s = b64.trim().replace(/\s+/g, '').replace(/-/g, '+').replace(/_/g, '/');
    const pad = s.length % 4;
    if (pad) s += '='.repeat(4 - pad);
    return s;
}

/**
 * Converts a (potentially URL-safe) Base64 string to a Uint8Array.
 * @param {string} base64 - The Base64 string.
 * @returns {Uint8Array} The decoded byte array.
 */
function base64ToUint8Array(base64) {
    try {
        const norm = normalizeB64(base64);
        const bin = atob(norm);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        return bytes;
    } catch (e) {
        // Signal "bad base64" by returning an empty array
        return new Uint8Array(0);
    }
}

/**
 * Converts a Uint8Array to a URL-safe Base64 string.
 * @param {Uint8Array} bytes - The byte array to encode.
 * @returns {string} A URL-safe Base64 string.
 */
function uint8ArrayToBase64(bytes) {
    let bin = '';
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// --- End of new helper functions ---


export const cryptoHelpers = {
    /**
     * Derives a cryptographic key from a password and salt using PBKDF2.
     * @param {string} password - The user's password.
     * @param {Uint8Array} salt - A random salt.
     * @returns {Promise<CryptoKey>} The derived AES-GCM key.
     */
    async deriveKey(password, salt) {
        const enc = new TextEncoder();
        const importedKey = await window.crypto.subtle.importKey(
            'raw',
            enc.encode(password),
            { name: 'PBKDF2' },
            false,
            ['deriveKey']
        );
        return window.crypto.subtle.deriveKey(
            { name: 'PBKDF2', salt, iterations: 100000, hash: 'SHA-256' },
            importedKey,
            { name: 'AES-GCM', length: 256 },
            true,
            ['encrypt', 'decrypt']
        );
    },

    /**
     * Encrypts a plaintext string using a given key and AES-GCM.
     * @param {string} plaintext - The string to encrypt.
     * @param {CryptoKey} key - The encryption key.
     * @returns {Promise<string>} The URL-safe base64-encoded ciphertext.
     */
    async encrypt(plaintext, key) {
        const enc = new TextEncoder();
        const iv = window.crypto.getRandomValues(new Uint8Array(12)); // 96-bit IV
        const encryptedContent = await window.crypto.subtle.encrypt(
            { name: 'AES-GCM', iv },
            key,
            enc.encode(plaintext)
        );
        // Prepend the IV to the ciphertext for use in decryption
        const encryptedBytes = new Uint8Array([...iv, ...new Uint8Array(encryptedContent)]);
        
        // UPDATED: Use the URL-safe base64 encoder
        return uint8ArrayToBase64(encryptedBytes);
    },

    /**
     * Decrypts a base64-encoded ciphertext using a given key.
     * @param {string} ciphertextB64 - The (potentially URL-safe) base64-encoded ciphertext.
     * @param {CryptoKey} key - The decryption key.
     * @returns {Promise<string|null>} The decrypted plaintext, or null if decryption fails.
     */
    async decrypt(ciphertextB64, key) {
        try {
            // UPDATED: Use the URL-safe base64 decoder
            const encryptedBytes = base64ToUint8Array(ciphertextB64);
            if (encryptedBytes.length <= 12) {
                console.error("Decryption failed: Ciphertext too short.");
                return null;
            }

            const iv = encryptedBytes.slice(0, 12);
            const data = encryptedBytes.slice(12);
            const decryptedContent = await window.crypto.subtle.decrypt(
                { name: 'AES-GCM', iv },
                key,
                data
            );
            return new TextDecoder().decode(decryptedContent);
        } catch (e) {
            console.error("Decryption failed:", e);
            return null; // Return null to indicate failure
        }
    },

    /**
     * Generates a new persistent data key for a user.
     * @returns {Promise<CryptoKey>} A new AES-GCM key.
     */
    async generateDataKey() {
        return window.crypto.subtle.generateKey(
            {
                name: "AES-GCM",
                length: 256,
            },
            true, // a boolean value indicating whether the key can be extracted from the CryptoKey object
            ["encrypt", "decrypt"]
        );
    }
};
